// frontend/src/components/reports/reportDefs.ts
//
// Registro de los tres reports de cliente. Fuente única para la cabecera de la
// tabla, el menú de columnas, el botón Σ, el pie de totales y el Excel: si una
// columna se declara aquí, aparece en todos ellos sin tocar nada más.
//
// DOS REGLAS QUE PARECEN CAPRICHOS Y NO LO SON. Estos reports los lee Sterling /
// Armaf, no nosotros, y los coteja contra un Excel que él mismo definió:
//
// 1. Los rótulos son LITERALMENTE los de su Excel, con sus almohadillas y sus
//    paréntesis: `AIR-VAL INVOICE #`, no `AIRVAL INVOICE`. Y van en inglés
//    siempre, independientemente del selector ES/EN de la barra lateral — ese
//    selector es para nuestra interfaz, no para el contenido del informe. Por eso
//    viven aquí como literales y no como claves i18n.
// 2. El orden de SUS columnas es intocable, y las nuestras de control van siempre
//    DESPUÉS, nunca intercaladas.
//
// `level` dice a qué nivel de la jerarquía tiene valor cada columna, verificado
// contra los datos: pintar un campo de cabecera en las sublíneas llenaría la tabla
// de guiones, y al revés escondería el dato.
import type { TFunction } from 'i18next'
import type { AggFn, ReportKey } from '@/types'
import { DECIMALES_CONSUMO_UNITARIO } from './formato'

/**
 * Faltante por debajo del cual la cifra es redondeo del ERP y no una necesidad
 * de compra.
 *
 * Medido el 2026-08-30 contra el dato real: 46 materiales con faltante de una
 * unidad o más, y 5 por debajo —0,002 · 0,027 · 0,049 · 0,068 · 0,206—. No hay
 * zona gris entre los dos grupos, así que el corte en 1 no arbitra nada.
 */
export const UNIDAD_MINIMA_FALTANTE = 1

/**
 * Desvío de consumo, en porcentaje, por encima del cual la cifra pide una mirada.
 *
 * El corte va en PORCENTAJE y no en cantidad porque en el mismo informe conviven
 * componentes de 5.906 kg y de 0,04 kg: un umbral en kilos no significaría nada.
 * Con el 1 %, en el producto que el cliente puso como ejemplo quedan fuera sus
 * cuatro aceites (+0,04 % a +0,80 %) y queda dentro el etanol (+4,79 %), que es
 * exactamente la lectura que este informe existe para enseñar.
 */
export const PCT_DESVIO_RELEVANTE = 1

export type ReportColKind =
  | 'text'
  | 'num'
  | 'date'
  | 'pct'
  /** Marca visible solo cuando el flag vale `flagOn`; el resto de filas en blanco. */
  | 'flag'
  /** Número visible solo a partir de `minToShow`; por debajo, en blanco. */
  | 'count'

/** A qué nivel de la jerarquía tiene valor la columna. */
export type ReportColLevel = 'header' | 'detail' | 'both'

export interface ReportCol {
  /** Campo de la fila. Es también la clave que se persiste en localStorage. */
  key: string
  /**
   * Rótulo, en inglés y con el texto exacto del Excel del cliente.
   *
   * No pasa por i18n a propósito: es el mismo en pantalla y en el fichero, y no
   * debe cambiar con el idioma de la interfaz. Ver la regla 1 de la cabecera.
   */
  label: string
  /**
   * `client` son las columnas de su Excel, `control` las nuestras (visibles, pero
   * siempre después de las suyas) y `technical` las de diagnóstico, ocultas por
   * defecto y activables desde el menú de columnas.
   */
  tier: 'client' | 'control' | 'technical'
  kind: ReportColKind
  level?: ReportColLevel        // default 'both'
  /**
   * Con `level: 'detail'` y varios tipos de detalle, a CUÁL de ellos pertenece.
   *
   * En consumo de producción el detalle son dos cosas distintas —lotes de granel
   * y componentes— y no comparten ni una columna: LITRES USED no existe en un
   * componente y BOM QTY no existe en un lote. Declararlo permite rotular encima
   * de cada tramo sólo sus columnas, que es lo que convierte 38 columnas medio
   * vacías en dos tablas legibles una debajo de la otra.
   *
   * Sin declarar, la columna vale para todos los tipos de detalle.
   */
  detailType?: string
  /**
   * La celda se colorea según su valor, no siempre igual.
   *
   * `deviation` la pinta cuando el desvío pasa de `PCT_DESVIO_RELEVANTE`: rojo si
   * se consumió más de lo previsto, verde si menos. Es lo único que da color a un
   * informe de 38 columnas de números, y de paso enseña de un vistazo lo que el
   * informe existe para enseñar.
   */
  tone?: 'deviation'
  /**
   * Campo que APAGA el color de `tone`: cuando trae valor, la celda va sin pintar.
   *
   * Un componente sustituido sale con −100 % —previsto 977 kg, real 0— y el verde
   * de «se consumió menos de lo previsto» ahí miente: no se ahorró nada, se cambió
   * de material y el sustituto está en otra fila. Sin esto, las dos filas de cada
   * sustitución son las más llamativas de la tabla y las dos dicen lo contrario de
   * lo que pasó.
   */
  toneSuppressedBy?: string
  /**
   * Rótulo de cada valor de una columna de texto, por valor crudo.
   *
   * Escotilla para cuando la palabra que el cliente tiene acordada en su Excel no
   * es la que emite la vista. Hoy no la usa nadie: las vistas emiten el
   * vocabulario del fichero —`FRAGRANCE`, `IN BOM, NOT USED`— y no hay nada que
   * traducir. Se conserva porque el día que el cliente pida otra palabra, esto
   * evita tocar SQL. Un valor que no esté en el mapa se pinta tal cual.
   */
  valueLabels?: Record<string, string>
  /**
   * Prefijo i18n de los valores, para la PANTALLA. `valueLabels` es el del Excel.
   *
   * Mismo reparto que los rótulos de columna: el fichero lleva el inglés del
   * cliente (`label`, y `valueLabels` si hiciera falta) y la pantalla sigue el
   * selector ES/EN (`reports.cols.*`, este prefijo). Es lo que mantiene la
   * interfaz española en español aunque el dato llegue en inglés. Un valor sin
   * traducción cae en `valueLabels` y, si tampoco está, se pinta crudo.
   */
  valueLabelPrefix?: string
  decimals?: number
  defaultAgg?: AggFn
  /**
   * Numérica pero NO sumable: sin botón Σ y sin fila de pie.
   *
   * Para las cifras que vienen repetidas por diseño. `numPedidosVenta` es el
   * número de pedidos del ARTÍCULO y llega idéntico en cada cabecera suya, así
   * que un total diría 9 donde hay 3 pedidos. El dato es correcto y la suma no,
   * y sin esto la tabla ofrece el botón que produce la cifra falsa.
   */
  noAgg?: true
  /** Ancho mínimo en píxeles para la columna en pantalla. */
  minW?: number
  /**
   * La columna se queda fija al desplazarse en horizontal.
   *
   * Es por columna y no «las N primeras» porque lo que hay que conservar a la
   * vista es la IDENTIDAD de la fila, y no siempre abre la tabla: en materia
   * prima el código de artículo va detrás del proveedor, y congelar hasta él
   * arrastraría al proveedor —170 px— dentro del bloque fijo sin necesidad.
   *
   * Las congeladas no tienen por qué ser contiguas. Cuando no lo son, las de en
   * medio pasan por debajo al desplazar y las fijas quedan pegadas entre sí, que
   * es exactamente lo que se quiere ver.
   */
  frozen?: true
  /** Ancho de la columna en el Excel (caracteres). */
  excelWidth?: number
  /** Valor del flag que hace visible la marca, en `kind: 'flag'`. */
  flagOn?: 0 | 1
  /** Texto de la marca en `kind: 'flag'`. */
  flagText?: string
  /** `alert` pinta la marca en ámbar; por defecto neutra. */
  flagTone?: 'neutral' | 'alert'
  /**
   * La celda lleva un CÓDIGO DE ARTÍCULO y hay que formatearlo por rol.
   *
   * Un usuario avp-only ve los códigos Armaf sin su letra de tipo (`QARF…` →
   * `ARF…`, ver `utils/fmtArticulo.ts`). Esto tiene que ir marcado en la columna
   * Y en su filtro a la vez: si sólo se marcase uno, la tabla y el desplegable
   * dirían cosas distintas del mismo artículo.
   */
  esArticulo?: boolean
  /** Mínimo a partir del cual se muestra el número, en `kind: 'count'`. */
  minToShow?: number
  /**
   * Clave i18n de un tooltip en la cabecera. Este SÍ va traducido: es ayuda para
   * quien opera la aplicación, no contenido del informe.
   */
  tooltipKey?: string
  /**
   * La columna existe SOLO para el Excel: no se pinta en pantalla ni aparece en el
   * menú de columnas, pero se exporta siempre.
   *
   * Es el caso de `SECTION` en producto terminado: en pantalla es una banda que
   * separa los dos bloques, y una banda no se exporta — el fichero necesita la
   * columna explícita.
   */
  excelOnly?: boolean
}

/** `multiText` y `multiSelect` son las versiones multi-valor de `text` y
 *  `select`: varios términos combinados con OR dentro del mismo filtro. Con un
 *  solo valor se comportan exactamente igual que sus versiones simples —
 *  subcadena y igualdad respectivamente—, así que migrar un filtro no cambia lo
 *  que devuelve. */
export type ReportFilterKind =
  | 'text' | 'select' | 'bool' | 'dateWindow' | 'multiText' | 'multiSelect'

export interface ReportFilterSpec {
  /** Clave en el estado de filtros y en la query string. */
  key: string
  labelKey: string
  kind: ReportFilterKind
  /**
   * Nivel al que se evalúa el predicado. Determina la semántica de bloque: un
   * filtro de cabecera descarta el bloque entero; uno de detalle lo conserva si
   * alguna sublínea casa.
   */
  scope: 'header' | 'detail'
  /** Campo evaluado en `text` / `select` / `bool`. */
  field?: string
  /** Campos de inicio y fin de la ventana en `dateWindow`. */
  fieldFrom?: string
  fieldTo?: string
  /** El filtro es de códigos de artículo: se formatean por rol, igual que su
   *  columna. Ver `esArticulo` en `ReportColSpec`. */
  esArticulo?: boolean
  /**
   * Campo con el NOMBRE de lo que identifica `field`, en los filtros de
   * catálogo. Un desplegable de códigos a secas obliga a saberse la referencia
   * de memoria: con esto el buscador enseña el nombre, se puede buscar por él y
   * el chip lo lleva en el tooltip.
   */
  descripcionField?: string
  /**
   * Rótulo de las opciones de un `select`, por valor. Solo cambia lo que se lee:
   * al predicado viaja el valor de la vista, que está en español.
   */
  optionLabels?: Record<string, string>
  /**
   * Prefijo i18n de los rótulos de opción, cuando tienen que seguir el idioma de
   * la UI: se busca `<prefijo>.<valor>` y se cae a `optionLabels` si no está.
   */
  optionLabelPrefix?: string
}

export interface ReportLevelOption {
  /** Valor de `tipoFila`. */
  tipoFila: string
  labelKey: string
}

/**
 * Dato de resumen que se pinta junto al expansor de un bloque PLEGADO.
 *
 * Plegado, un bloque solo mostraba sus columnas de cabecera: en la cobertura de
 * materiales eso son el código y el nombre, y nada más. El resumen responde
 * «¿merece la pena que lo despliegue?» sin desplegarlo.
 */
export interface ReportSummaryChip {
  /** Clave i18n; recibe `count` y `formattedValue`. */
  labelKey: string
  value: number
  /** `alert` ámbar, `danger` rojo: para lo que frena. */
  tone?: 'neutral' | 'alert' | 'danger'
}

export interface ReportDef {
  key: ReportKey
  /** Segmento de ruta bajo /reports. */
  route: string
  titleKey: string
  tabKey: string
  /** Clave i18n de la unidad que se cuenta, en el dominio de cada pantalla. */
  unitKey: string
  /** `tipoFila` de las filas de cabecera. */
  headerType: string
  /** `tipoFila` de las filas de detalle, en el orden en que aparecen. */
  detailTypes: string[]
  /**
   * Campos de la CABECERA que identifican el bloque en el negocio.
   *
   * Es con lo que se compone la clave de las marcas de fila. NO se usa
   * `ordenBloque`: es la columna del ORDER BY de la vista, un ordinal de
   * posición, y al renumerarse una vista las marcas guardadas se mudan a otras
   * filas sin dar error. El detalle está en `marcasReport.ts`.
   *
   * Tienen que identificar el bloque de forma única dentro del informe. Si no lo
   * hacen, dos bloques comparten marca y el centinela de `ReportsPage` lo avisa
   * por consola.
   */
  blockKeyFields: string[]
  /**
   * Campos que identifican una SUBLÍNEA, por `tipoFila`.
   *
   * Sin entrada para un tipo, sus sublíneas caen a la clave de reserva por
   * posición, que es inestable pero nunca se confunde con otra fila.
   */
  detailKeyFields?: Record<string, string[]>
  /** Opciones del selector «Totales de», que es de opción única a propósito. */
  levelOptions: ReportLevelOption[]
  cols: ReportCol[]
  /**
   * Campo por el que deduplicar antes de agregar. Solo la cobertura de materiales
   * lo necesita: repite cada componente bajo cada terminado que lo usa, así que
   * sin esto los totales de materia prima salen multiplicados.
   */
  dedupeBy?: string
  /** Campo sobre el que ofrecer COUNT DISTINCT, si tiene sentido en el report. */
  distinctField?: string
  /**
   * Campo que parte el report en secciones NO sumables entre sí. Cuando está, el
   * pie totaliza por sección y nunca cruzándolas.
   *
   * Es el caso de producto terminado: el stock reservado de un granel está en
   * litros o en botellas montadas, y el de un terminado en unidades de perfume. No
   * son la misma magnitud, y sumarlas en la misma celda rompe el cuadre que el
   * cliente usa para validar el informe entero.
   */
  sectionField?: string
  /** Rótulo de cada sección, en inglés y con el texto del cliente. */
  sectionLabels?: Record<string, string>
  /**
   * Resumen del bloque plegado. Devolver `[]` no pinta nada.
   *
   * Se pinta como chips DENTRO de la primera columna congelada, junto al valor
   * que identifica el bloque. Antes tenía canaleta propia de 136 a 184 px, y esa
   * canaleta iba congelada: gastaba el espacio más caro de la tabla para pintar
   * unos avisos que solo aparecen en los bloques plegados, y en las sublíneas no
   * pintaba nada.
   */
  blockSummary?: (
    header: Record<string, unknown>,
    details: Record<string, unknown>[],
  ) => ReportSummaryChip[]
  filters: ReportFilterSpec[]
  /**
   * El detalle se pinta como SUBTABLAS anidadas, una por tipo, en vez de como
   * filas de la rejilla maestra.
   *
   * Para cuando los tipos de detalle no comparten columnas. En consumo de
   * producción son dos cosas distintas —lotes de granel y componentes— y ninguna
   * de sus columnas vale para la otra: en una sola rejilla, cada fila de lote
   * dejaba en blanco las nueve del componente y al revés, con 35 columnas de
   * ancho para que ninguna fila llenara más de veinte.
   *
   * Anidando, cada nivel lleva su propia cabecera con SUS columnas, la maestra se
   * queda con las del producto, y los huecos desaparecen porque ya no existen.
   * A cambio, las columnas del detalle salen de la rejilla maestra: no se pueden
   * congelar ni totalizar en el pie de arriba, y cada subtabla lleva su propio
   * total. Es el reparto que ya hacen las líneas de un pedido de venta.
   */
  nestedDetail?: true
  /**
   * Los rótulos de la cabecera pueden ocupar dos líneas.
   *
   * Sin esto, el `nowrap` obliga a que cada columna mida al menos lo que su
   * rótulo entero, y en un informe de cifras eso es casi siempre más que el
   * número: `TOTAL (KG)` pedía 140 px para «8.465,211», que ocupa 70. Con 19
   * columnas así, dos tercios de la tabla quedaban fuera de la vista y cada celda
   * era mayormente relleno.
   *
   * Va por report y no para todos porque en los tres primeros las cabeceras de
   * una línea ya están validadas con el cliente.
   */
  wrapHeaders?: true
  /** Sufijo de las claves de localStorage (columnas visibles y agregaciones). */
  storageKey: string
  /** Nombre base del fichero Excel. */
  excelBase: string
}

// ── Materia prima ─────────────────────────────────────────────────────────────
//
// El orden de las columnas del cliente ya coincidía con su Excel: no se toca.

const RAW_MATERIALS: ReportDef = {
  key: 'rawMaterials',
  route: 'raw-materials',
  titleKey: 'reports.rawMaterials.title',
  tabKey: 'reports.rawMaterials.tab',
  unitKey: 'reports.rawMaterials.unit',
  headerType: 'HEADER',
  detailTypes: ['BATCH'],
  blockKeyFields: ['airvalInvoiceNo', 'sterlingPo', 'itemCode'],
  detailKeyFields: { BATCH: ['batchNo'] },
  levelOptions: [
    { tipoFila: 'HEADER', labelKey: 'reports.rawMaterials.levelHeader' },
    { tipoFila: 'BATCH', labelKey: 'reports.rawMaterials.levelBatch' },
  ],
  storageKey: 'reports.rawMaterials',
  excelBase: 'airval_report_raw_materials',
  distinctField: 'itemCode',
  blockSummary: (header, details) => {
    const chips: ReportSummaryChip[] = [
      { labelKey: 'reports.summary.batches', value: details.length },
    ]
    // Lotes cuyas cantidades cuelgan de otro pedido: explican por qué la cabecera
    // puede no cuadrar con la suma de lo que se ve.
    const noAtribuidos = details.filter(d => Number(d.esLoteAtribuido ?? 1) === 0).length
    if (noAtribuidos) {
      chips.push({ labelKey: 'reports.summary.unattributed', value: noAtribuidos, tone: 'alert' })
    }
    if (Number(header.haySobreservicio ?? 0) === 1) {
      chips.push({ labelKey: 'reports.summary.overReceived', value: 1, tone: 'alert' })
    }
    return chips
  },
  filters: [
    { key: 'sterlingPo', labelKey: 'reports.filters.sterlingPo', kind: 'multiText', scope: 'header', field: 'sterlingPo' },
    { key: 'airvalInvoiceNo', labelKey: 'reports.filters.airvalInvoiceNo', kind: 'multiText', scope: 'header', field: 'airvalInvoiceNo' },
    { key: 'supplier', labelKey: 'reports.filters.supplier', kind: 'multiSelect', scope: 'header', field: 'supplier' },
    { key: 'itemCode', labelKey: 'reports.filters.itemCode', kind: 'multiText', scope: 'header', field: 'itemCode', descripcionField: 'description', esArticulo: true },
    {
      key: 'receipt', labelKey: 'reports.filters.receiptRange', kind: 'dateWindow', scope: 'header',
      fieldFrom: 'firstReceiptDate', fieldTo: 'lastReceiptDate',
    },
    { key: 'haySobreservicio', labelKey: 'reports.filters.haySobreservicio', kind: 'bool', scope: 'header', field: 'haySobreservicio' },
  ],
  cols: [
    // ── Columnas del cliente, en su orden ─────────────────────────────────────
    // 225, y no los 145 de antes: aquí van ahora los chips del bloque plegado, con
    // el ancho que ha soltado la canaleta.
    { key: 'airvalInvoiceNo', label: 'AIR-VAL INVOICE #', tier: 'client', kind: 'text', frozen: true, minW: 225, excelWidth: 20 },
    { key: 'sterlingPo', label: 'STERLING PO#', tier: 'client', kind: 'text', level: 'header', frozen: true, minW: 125, excelWidth: 16 },
    { key: 'supplier', label: 'SUPPLIER', tier: 'client', kind: 'text', level: 'header', minW: 170, excelWidth: 26 },
    // Congelada aunque el proveedor, que va delante, no lo esté: al desplazarse,
    // el proveedor pasa por debajo y el código queda pegado al pedido.
    { key: 'itemCode', label: 'ITEM CODE', tier: 'client', kind: 'text', esArticulo: true, frozen: true, minW: 130, excelWidth: 18 },
    { key: 'description', label: 'DESCRIPTION', tier: 'client', kind: 'text', level: 'header', minW: 230, excelWidth: 38 },
    { key: 'batchNo', label: 'BATCH', tier: 'client', kind: 'text', level: 'detail', minW: 150, excelWidth: 22 },
    { key: 'orderQty', label: 'ORDER QTY', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 120, excelWidth: 14 },
    { key: 'receivedQty', label: 'RECEIVED QTY', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 130, excelWidth: 14 },
    { key: 'pendingToReceiveQty', label: 'PENDING TO RECEIVE QTY', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 170, excelWidth: 18 },
    { key: 'rejectedQty', label: 'REJECTED QTY (QUALITY ISSUE)', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 195, excelWidth: 22, tooltipKey: 'reports.tooltips.rejectedQty' },
    { key: 'consumedQty', label: 'CONSUMED QTY', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 130, excelWidth: 14 },
    { key: 'reservedQty', label: 'RESERVED QTY', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 130, excelWidth: 14 },
    { key: 'physicalStock', label: 'PHYSICAL STOCK', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 140, excelWidth: 16 },
    { key: 'qcStock', label: 'QC STOCK', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 120, excelWidth: 14, tooltipKey: 'reports.tooltips.qcStock' },
    { key: 'firstReceiptDate', label: 'FIRST RECEIPT', tier: 'client', kind: 'date', minW: 125, excelWidth: 16 },
    { key: 'lastReceiptDate', label: 'LAST RECEIPT', tier: 'client', kind: 'date', minW: 125, excelWidth: 16 },

    // ── Nuestras columnas de control, siempre después de las del cliente ──────
    //
    // LA CUENTA DEL FALTANTE, en orden de lectura y contigua. Son seis columnas
    // que sólo significan algo juntas:
    //
    //   PHYSICAL STOCK − RESERVED ON STOCK          = FREE STOCK
    //   FREE STOCK + INCOMING QTY − PRODUCTION PLAN = PROJECTED BALANCE
    //   PROJECTED BALANCE < 0                       → SHORTAGE QTY
    //
    // Separarlas dejaría al cliente con un balance negativo salido de la nada.
    // Van delante de las marcas porque continúan la lectura numérica; las marcas
    // son anotaciones sobre las columnas del cliente y se leen bien al final.
    {
      key: 'reservedEffectiveQty', label: 'RESERVED ON STOCK', tier: 'control', kind: 'num',
      defaultAgg: 'sum', minW: 165, excelWidth: 18,
      tooltipKey: 'reports.tooltips.reservedOnStock',
    },
    { key: 'availableStock', label: 'FREE STOCK', tier: 'control', kind: 'num', defaultAgg: 'sum', minW: 140, excelWidth: 16 },
    { key: 'expectedInbound', label: 'INCOMING QTY', tier: 'control', kind: 'num', defaultAgg: 'sum', minW: 145, excelWidth: 16 },
    {
      key: 'planDemand', label: 'PRODUCTION PLAN QTY', tier: 'control', kind: 'num',
      defaultAgg: 'sum', minW: 180, excelWidth: 20,
      tooltipKey: 'reports.tooltips.planDemand',
    },
    {
      key: 'totalAvailableErp', label: 'PROJECTED BALANCE', tier: 'control', kind: 'num',
      defaultAgg: 'sum', minW: 165, excelWidth: 18,
      tooltipKey: 'reports.tooltips.projectedBalance',
    },
    {
      key: 'shortageQty', label: 'SHORTAGE QTY', tier: 'control', kind: 'num', level: 'header',
      defaultAgg: 'sum', minW: 145, excelWidth: 16,
      tooltipKey: 'reports.tooltips.shortageQty',
    },

    {
      key: 'haySobreservicio', label: 'OVER-RECEIPT', tier: 'control', kind: 'flag', level: 'header',
      flagOn: 1, flagText: 'OVER', flagTone: 'alert', minW: 125, excelWidth: 14,
      tooltipKey: 'reports.tooltips.overReceipt',
    },
    {
      key: 'numPedidosDelLote', label: 'SHARED BATCH', tier: 'control', kind: 'count', level: 'detail',
      minToShow: 2, decimals: 0, minW: 130, excelWidth: 14,
      tooltipKey: 'reports.tooltips.sharedBatch',
    },
    {
      key: 'esLoteAtribuido', label: 'ATTRIBUTED', tier: 'control', kind: 'flag', level: 'detail',
      // Se marca cuando vale 0, que es el caso raro y el que hay que explicar: las
      // cantidades del lote cuelgan de otro pedido.
      flagOn: 0, flagText: 'OTHER PO', flagTone: 'alert', minW: 125, excelWidth: 14,
      tooltipKey: 'reports.tooltips.attributed',
    },

    // ── Técnicas ──────────────────────────────────────────────────────────────
    // Aquí estaban PO AMOUNT, CURRENCY, SUPPLIER CODE y MANAGEMENT TYPE. Se
    // retiraron: son el coste de compra de Air-Val y su proveedor, y este report
    // lo consulta el cliente, que entra con el rol Empresa.AVP. `technical` sólo
    // las ocultaba —el rótulo se lee en el menú de columnas y la elección se
    // persiste—, así que el backend tampoco las envía ya. Siguen en la vista de
    // Fabric. Ver `docs/reports-costes.md`.
    //
    // Sólo dos, y el listón para añadir una tercera es alto: este report lo
    // consulta el CLIENTE y el menú de columnas NO filtra por rol, así que lo que
    // se declare aquí Sterling lo puede activar y exportar. El criterio es «cubre
    // una necesidad suya», no «puede servirle a alguien alguna vez».
    //
    // Retiradas en agosto de 2026: los diagnósticos internos y nuestros cuadres
    // —una diferencia distinta de cero se lee como un error del informe—, lo
    // derivable de columnas que ya están (% recibido, año del pedido, y el nº de
    // lotes, que se cuenta solo mirando las sublíneas) y lo constante: `color`
    // valía '01' y `estilo` '00' en las 385 filas, y `empresa` siempre 'avp'.
    // Todo sigue en la vista de Fabric.
    //
    // ENTREGA PREVISTA se queda aunque no esté en la plantilla del cliente:
    // responde a «cuándo llega el resto», que es el espíritu de su cuarta
    // petición, y viene poblada en las 385 filas.
    { key: 'fechaCreacion', label: 'PO CREATED', tier: 'technical', kind: 'date', level: 'header', minW: 120, excelWidth: 14 },
    { key: 'fechaEntregaPrevista', label: 'EXPECTED DELIVERY', tier: 'technical', kind: 'date', level: 'header', minW: 140, excelWidth: 16 },
  ],
}

// ── Producto terminado ────────────────────────────────────────────────────────
//
// `PRDN ORDER` va entre `SALES ORDER` e `INV #`, no al final: el cliente lee de
// izquierda a derecha «pedido de venta → orden de producción → factura», que es la
// cadena de trazabilidad, y al final de la tabla esa lectura desaparece detrás del
// desplazamiento horizontal.
//
// EL GRANO ES ARTÍCULO × PEDIDO DE VENTA. Un artículo con tres pedidos abiertos
// trae tres cabeceras, y `SALES ORDER` está informada en la cabecera. Antes había
// una cabecera por artículo y `AIR-VAL PO#` llevaba el literal `'007'`, que
// afirmaba que todo iba contra un solo pedido: el cliente lo cazó sobre
// `ARF32101997`, cuyos 500.000 son 300.000 + 100.000 + 100.000 de tres pedidos.
//
// Consecuencia para leer esta tabla: `TOTAL QTY`, `PENDING TO PRODUCE`,
// `PENDING TO SERVE` y `OPEN PRDN QTY` van POR PEDIDO y suman bien en el pie. Los
// stocks no: el almacén tiene un stock del artículo, no comprometido a un pedido,
// así que aterrizan en una sola cabecera —la marcada con `STOCK HERE`— y en las
// demás van vacíos. Un vacío ahí es «está en otra fila», no «no hay».

const FG: ReportDef = {
  key: 'fg',
  route: 'fg',
  titleKey: 'reports.fg.title',
  tabKey: 'reports.fg.tab',
  unitKey: 'reports.fg.unit',
  headerType: 'HEADER',
  detailTypes: ['INVOICE', 'WORK_ORDER'],
  // `salesOrder` entra en la identidad porque el bloque ES artículo × pedido: sin
  // él, las tres cabeceras de `ARF32101997` compartirían marca. `seccion` está
  // porque un semielaborado no tiene pedido y su clave quedaría a medias.
  blockKeyFields: ['seccion', 'fgCode', 'salesOrder'],
  detailKeyFields: { INVOICE: ['invNo'], WORK_ORDER: ['prdnOrder'] },
  levelOptions: [
    { tipoFila: 'HEADER', labelKey: 'reports.fg.levelHeader' },
    { tipoFila: 'INVOICE', labelKey: 'reports.fg.levelInvoice' },
    { tipoFila: 'WORK_ORDER', labelKey: 'reports.fg.levelOp' },
  ],
  storageKey: 'reports.fg',
  excelBase: 'airval_report_fg',
  distinctField: 'fgCode',
  sectionField: 'seccion',
  sectionLabels: { 'FINISHED GOOD': 'FINISHED GOODS', 'SEMI-FINISHED': 'SEMI-FINISHED' },
  blockSummary: (_header, details) => {
    const chips: ReportSummaryChip[] = []
    const facturas = details.filter(d => d.tipoFila === 'INVOICE').length
    const ops = details.filter(d => d.tipoFila === 'WORK_ORDER').length
    if (facturas) chips.push({ labelKey: 'reports.summary.invoices', value: facturas })
    if (ops) chips.push({ labelKey: 'reports.summary.prodOrders', value: ops })
    return chips
  },
  filters: [
    {
      key: 'seccion', labelKey: 'reports.filters.seccion', kind: 'multiSelect', scope: 'header', field: 'seccion',
      // Se lee en inglés; al predicado viaja el valor de la vista.
      optionLabelPrefix: 'reports.seccion',
      optionLabels: { 'FINISHED GOOD': 'Finished goods', 'SEMI-FINISHED': 'Semi-finished' },
    },
    { key: 'fgCode', labelKey: 'reports.filters.fgCode', kind: 'multiText', scope: 'header', field: 'fgCode', descripcionField: 'description', esArticulo: true },
    // Filtro de cabecera, y de cabecera precisamente porque ahora la cabecera ES
    // un pedido. La queja del cliente venía de un pedido concreto («your 007
    // sales order»), y con el mismo artículo repartido en tres filas hacía falta
    // poder ir a una. Lleva `airvalPo` de descripción para que el desplegable
    // enseñe «26APPV000007 · FG ORDER 1» y no sólo el número.
    { key: 'salesOrder', labelKey: 'reports.filters.salesOrder', kind: 'multiText', scope: 'header', field: 'salesOrder', descripcionField: 'airvalPo' },
  ],
  cols: [
    // Banda en pantalla, columna en el Excel: una banda visual no se exporta.
    { key: 'seccion', label: 'SECTION', tier: 'client', kind: 'text', excelWidth: 16, excelOnly: true },

    // ── Columnas del cliente, en su orden ─────────────────────────────────────
    // Lleva el `customerref` del pedido («FG ORDER 1»…), con el `salesid` de
    // reserva donde venga vacío. 240 y no 205 porque ya no cabe «007» sino un
    // rótulo de pedido MÁS los chips del bloque plegado —«8 facturas · 5 OP»— en
    // la misma celda, y con reparto fijo lo que no cabe se sale encima de la
    // columna siguiente.
    { key: 'airvalPo', label: 'AIR-VAL PO#', tier: 'client', kind: 'text', level: 'header', frozen: true, minW: 240, excelWidth: 16, tooltipKey: 'reports.tooltips.airvalPo' },
    { key: 'fgCode', label: 'FG CODE', tier: 'client', kind: 'text', esArticulo: true, frozen: true, minW: 130, excelWidth: 18 },
    { key: 'description', label: 'DESCRIPTION', tier: 'client', kind: 'text', level: 'header', minW: 230, excelWidth: 38 },
    { key: 'totalQty', label: 'TOTAL QTY', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 120, excelWidth: 14 },
    // `level: 'both'`, no `'detail'`: con el grano nuevo la cabecera ES un pedido y
    // tiene que decir cuál. Mientras estuvo declarada como de detalle, el dato
    // llegaba en la cabecera y `colAppliesTo` lo descartaba — celda vacía, sin
    // error, que es la forma en que este módulo falla.
    { key: 'salesOrder', label: 'SALES ORDER', tier: 'client', kind: 'text', minW: 140, excelWidth: 18 },
    { key: 'prdnOrder', label: 'PRDN ORDER', tier: 'client', kind: 'text', level: 'detail', minW: 140, excelWidth: 18 },
    { key: 'invNo', label: 'INV #', tier: 'client', kind: 'text', level: 'detail', minW: 110, excelWidth: 14 },
    // Son unidades de perfume, no kilos: `custinvoicetrans.qty` va en unidades de
    // inventario, que son piezas. El rótulo anterior, «QTY (KG)», era una lectura
    // falsa de un dato correcto.
    { key: 'qtyUnits', label: 'QTY (UNITS)', tier: 'client', kind: 'num', defaultAgg: 'sum', minW: 125, excelWidth: 14, tooltipKey: 'reports.tooltips.qtyUnits' },
    { key: 'reservedStock', label: 'RESERVED STOCK', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 145, excelWidth: 16 },
    { key: 'balanceFgInventory', label: 'BALANCE FG (INVENTORY)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 170, excelWidth: 20 },
    { key: 'pendingToProduce', label: 'PENDING TO PRODUCE', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 155, excelWidth: 18 },

    // ── Nuestras columnas de control ──────────────────────────────────────────
    // Sin estas, una fila de orden de producción solo muestra su número y la
    // cantidad declarada, y no dice nada.
    {
      key: 'prodEstado', label: 'PRDN STATUS', tier: 'control', kind: 'text',
      level: 'detail', minW: 150, excelWidth: 20,
      // La vista pasa a emitir los rótulos de D365 en inglés. El prefijo devuelve
      // el castellano a la interfaz española, que es lo que ya veía: el fichero
      // del cliente cambia de idioma, su pantalla no.
      valueLabelPrefix: 'reports.prodEstado',
    },
    { key: 'prodQtyPlanificada', label: 'PRDN PLANNED', tier: 'control', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 135, excelWidth: 16 },
    { key: 'prodQtyPendiente', label: 'PRDN PENDING', tier: 'control', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 135, excelWidth: 16 },
    // Comparada con PENDING TO PRODUCE revela pedido sin cubrir.
    { key: 'qtyOpsAbiertas', label: 'OPEN PRDN QTY', tier: 'control', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 140, excelWidth: 16, tooltipKey: 'reports.tooltips.openPrdnQty' },
    // La fecha comprometida del pedido. Con tres pedidos del mismo artículo en
    // pantalla es lo que los ordena de un vistazo: 24/07, 12/08, 21/08.
    { key: 'fechaEntrega', label: 'DELIVERY DATE', tier: 'control', kind: 'date', level: 'header', minW: 130, excelWidth: 14, tooltipKey: 'reports.tooltips.fechaEntrega' },
    // Se marca el 0, que es la cabecera cuyo SALDO EN INVENTARIO sale con GUION,
    // porque es ahí donde está la pregunta del cliente: ve un «—» y quiere saber
    // si es que no hay stock. La marca contesta que está en otra fila del mismo
    // artículo. (El NULL de una columna numérica se pinta como `NO_APLICA`, no
    // como celda en blanco: eso último sólo pasa en `flag` y `count`.)
    //
    // Marcar el 1 —la fila que sí lleva el dato— también se leería, pero se pinta
    // en 52 de las 77 cabeceras frente a 25, y un aviso que sale en la mayoría de
    // las filas deja de avisar. Es el mismo reparto que `esLoteAtribuido`, que
    // marca su 0 por la misma razón.
    //
    // La columna se apoya en que el flag y el NULL del saldo son complementarios
    // (`= 1` ⟺ el saldo NO es NULL, medido en los dos sentidos). Si eso se
    // desacoplara, la marca mentiría sobre qué celda está vacía sin dar error, y
    // por eso el verificador lo comprueba fila a fila.
    //
    // En SEMI-FINISHED llega NULL, no 0: allí hay una cabecera por artículo y no
    // hay filas hermanas entre las que repartir, así que la pregunta no se
    // plantea. `fmtCell` no pinta nada con NULL porque comprueba `esVacio` ANTES
    // que el valor — sin ese orden, `flagOn: 0` marcaría todos los semielaborados.
    {
      key: 'esStockAtribuido', label: 'ITEM STOCK', tier: 'control', kind: 'flag', level: 'header',
      flagOn: 0, flagText: 'OTHER ROW', minW: 130, excelWidth: 14,
      tooltipKey: 'reports.tooltips.esStockAtribuido',
    },
    // La otra pregunta, y la contraria: aquí la cifra es de esta OP y lo que puede
    // estar conjeturado es a qué pedido pertenece. Se marca el 1 porque es la
    // salvedad, aunque sea el caso mayoritario. `detailType` la mantiene fuera de
    // la cabecera de la subtabla de facturas, donde no significaría nada.
    {
      key: 'esOpEstimada', label: 'PO LINK', tier: 'control', kind: 'flag', level: 'detail',
      detailType: 'WORK_ORDER',
      flagOn: 1, flagText: 'ESTIMATED', flagTone: 'alert', minW: 125, excelWidth: 14,
      tooltipKey: 'reports.tooltips.esOpEstimada',
    },
    // `noAgg`: es el recuento de pedidos del ARTÍCULO y llega repetido en cada
    // cabecera suya, así que sumarlo daría 9 donde hay 3. Es justo la columna que
    // habría cazado el fallo del grano: llevaba tiempo llegando sin pintarse.
    { key: 'numPedidosVenta', label: 'SALES ORDERS (ITEM)', tier: 'control', kind: 'num', level: 'header', decimals: 0, noAgg: true, minW: 150, excelWidth: 18, tooltipKey: 'reports.tooltips.numPedidosVenta' },

    // ── Técnicas ──────────────────────────────────────────────────────────────
    { key: 'qtyPendienteServir', label: 'PENDING TO SERVE', tier: 'technical', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 145, excelWidth: 16 },
    { key: 'fechaFactura', label: 'INVOICE DATE', tier: 'technical', kind: 'date', level: 'detail', minW: 125, excelWidth: 14 },
    { key: 'importeFacturado', label: 'INVOICED AMOUNT', tier: 'technical', kind: 'num', level: 'detail', decimals: 2, defaultAgg: 'sum', minW: 145, excelWidth: 16 },
    { key: 'prodFechaInicio', label: 'PRDN START', tier: 'technical', kind: 'date', level: 'detail', minW: 120, excelWidth: 14 },
    { key: 'prodFechaFin', label: 'PRDN END', tier: 'technical', kind: 'date', level: 'detail', minW: 120, excelWidth: 14 },
  ],
}

// ── Cobertura de materiales ───────────────────────────────────────────────────
//
// Los stocks del terminado van justo detrás de su identificación y ANTES de los de
// materia prima: la pregunta que responde la pantalla es «cuánto terminado tengo y
// cuánto material me queda para hacer más», en ese orden.
//
// Cada CÓDIGO abre su pareja y su descripción va detrás —terminado primero,
// componente después—, y la línea de escandallo cierra el grupo de identidad. Es
// el orden que deja congelados los dos códigos, que es lo que hay que seguir
// viendo al desplazarse: el número de línea del escandallo no identifica nada
// fuera de su bloque.

const FG_TO_RM: ReportDef = {
  key: 'fgToRm',
  route: 'fg-to-rm',
  titleKey: 'reports.fgToRm.title',
  tabKey: 'reports.fgToRm.tab',
  unitKey: 'reports.fgToRm.unit',
  headerType: 'FG',
  detailTypes: ['COMPONENT'],
  blockKeyFields: ['fgCode'],
  detailKeyFields: { COMPONENT: ['itemCode', 'bomItem'] },
  levelOptions: [
    { tipoFila: 'FG', labelKey: 'reports.fgToRm.levelFg' },
    { tipoFila: 'COMPONENT', labelKey: 'reports.fgToRm.levelComponent' },
  ],
  storageKey: 'reports.fgToRm',
  excelBase: 'airval_report_fg_to_rm',
  dedupeBy: 'itemCode',
  distinctField: 'itemCode',
  blockSummary: (_header, details) => {
    const chips: ReportSummaryChip[] = [
      { labelKey: 'reports.summary.components', value: details.length },
    ]
    // CUÁNTOS COMPONENTES FALTAN, y no las unidades que cubre el que menos.
    //
    // El distintivo era «cubre N», el mínimo de UNIDADES CUBIERTAS. Esa columna
    // cambió de pregunta: ya no dice cuántas unidades cubre el stock, sino
    // cuántas MÁS se podrían fabricar después de honrar todo lo comprometido. Con
    // casi todo comprometido eso es cero en 45 de 52 terminados, así que el
    // distintivo salía en rojo casi siempre — y un aviso que sale casi siempre
    // deja de avisar, que es el mismo error que ya cometimos en la canaleta.
    //
    // El recuento de componentes con faltante sí discrimina: 15 terminados
    // quedan limpios y el resto va de 1 a 5 (medido el 2026-08-30). Además el
    // número dice algo por sí solo, no sólo el color.
    //
    // El corte en una unidad no es cosmético: por debajo hay faltantes de 0,002
    // y 0,068 unidades que son redondeo del ERP y no una necesidad de compra.
    const enFalta = details.filter(d => Number(d.rmShortageQty ?? 0) >= UNIDAD_MINIMA_FALTANTE).length
    if (enFalta) {
      chips.push({ labelKey: 'reports.summary.shortage', value: enFalta, tone: 'danger' })
    }
    const sinStock = details.filter(d => Number(d.rmFreeStock ?? NaN) === 0).length
    if (sinStock) {
      chips.push({ labelKey: 'reports.summary.noStock', value: sinStock, tone: 'alert' })
    }
    return chips
  },
  filters: [
    { key: 'fgCode', labelKey: 'reports.filters.fgCode', kind: 'multiText', scope: 'header', field: 'fgCode', descripcionField: 'fgName', esArticulo: true },
    { key: 'itemCode', labelKey: 'reports.filters.itemCode', kind: 'multiText', scope: 'detail', field: 'itemCode', descripcionField: 'componentDescription', esArticulo: true },
    { key: 'esComponenteLimitante', labelKey: 'reports.filters.esComponenteLimitante', kind: 'bool', scope: 'detail', field: 'esComponenteLimitante' },
    { key: 'esComponenteCompartido', labelKey: 'reports.filters.esComponenteCompartido', kind: 'bool', scope: 'detail', field: 'esComponenteCompartido' },
  ],
  cols: [
    // ── Columnas del cliente ──────────────────────────────────────────────────
    // 250 para alojar los chips del bloque plegado —cobertura, componentes sin
    // stock y recuento— con el ancho que ha soltado la canaleta.
    { key: 'fgCode', label: 'FG CODE', tier: 'client', kind: 'text', esArticulo: true, frozen: true, minW: 250, excelWidth: 18 },
    { key: 'fgName', label: 'FG NAME', tier: 'client', kind: 'text', level: 'header', minW: 230, excelWidth: 38 },
    { key: 'itemCode', label: 'ITEM CODE', tier: 'client', kind: 'text', level: 'detail', esArticulo: true, frozen: true, minW: 130, excelWidth: 18 },
    { key: 'componentDescription', label: 'COMPONENT DESCRIPTION', tier: 'client', kind: 'text', level: 'detail', minW: 235, excelWidth: 38 },
    // Decimal a propósito: `bom.linenum` de 365FO trae fracciones (1 · 1,5 · 1,75)
    // porque son líneas insertadas después, y redondear colapsaría componentes
    // distintos en el mismo número.
    { key: 'bomItem', label: 'BOM ITEM', tier: 'client', kind: 'num', level: 'detail', decimals: 2, minW: 105, excelWidth: 12 },
    { key: 'fgFreeStock', label: 'FG FREE STOCK', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 140, excelWidth: 16 },
    { key: 'fgQcStock', label: 'FG QC STOCK', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 130, excelWidth: 14 },
    { key: 'rmFreeStock', label: 'RM FREE STOCK', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 140, excelWidth: 16 },
    { key: 'rmQcStock', label: 'RM QC STOCK', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 130, excelWidth: 14 },
    { key: 'rmOverallFreeStock', label: 'RM OVERALL FREE STOCK', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 175, excelWidth: 20 },
    { key: 'rmFreePlusPendingPo', label: 'RM FREE + PENDING PO', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 170, excelWidth: 20 },
    { key: 'pendingPoQty', label: 'PENDING PO QUANTITY', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'sum', minW: 165, excelWidth: 18 },
    { key: 'consumoUnitario', label: 'QTY PER FG UNIT', tier: 'client', kind: 'num', level: 'detail', decimals: DECIMALES_CONSUMO_UNITARIO, minW: 150, excelWidth: 18 },
    { key: 'unidadesFgCubiertas', label: 'FG UNITS COVERED', tier: 'client', kind: 'num', level: 'detail', defaultAgg: 'min', minW: 150, excelWidth: 18 },

    // ── Nuestras columnas de control ──────────────────────────────────────────
    //
    // Aquí la cuenta NO se enseña entera, a diferencia de materia prima: sus dos
    // columnas intermedias no cuadran con el total en los artículos con distinta
    // unidad de compra e inventario, y el bueno es el total. Ver el modelo en
    // `backend/app/schemas/reports.py`.
    {
      key: 'rmTotalAvailable', label: 'PROJECTED BALANCE', tier: 'control', kind: 'num', level: 'detail',
      defaultAgg: 'sum', minW: 165, excelWidth: 18,
      tooltipKey: 'reports.tooltips.projectedBalanceRm',
    },
    {
      key: 'rmShortageQty', label: 'SHORTAGE QTY', tier: 'control', kind: 'num', level: 'detail',
      defaultAgg: 'sum', minW: 145, excelWidth: 16,
      tooltipKey: 'reports.tooltips.shortageQtyRm',
    },

    // El componente que cubre menos unidades y por tanto frena la producción: uno
    // por terminado. Es la columna más accionable de la pantalla, y por eso no
    // basta con el distintivo del resumen — un distintivo no se exporta, no se
    // ordena y no se filtra.
    {
      key: 'esComponenteLimitante', label: 'BOTTLENECK', tier: 'control', kind: 'flag', level: 'detail',
      flagOn: 1, flagText: 'BOTTLENECK', flagTone: 'alert', minW: 140, excelWidth: 14,
      tooltipKey: 'reports.tooltips.limitante',
    },
    {
      key: 'esComponenteCompartido', label: 'SHARED', tier: 'control', kind: 'flag', level: 'detail',
      flagOn: 1, flagText: 'SHARED', minW: 110, excelWidth: 12,
      tooltipKey: 'reports.tooltips.shared',
    },
    { key: 'numTerminadosQueLoUsan', label: 'USED IN N FG', tier: 'control', kind: 'num', level: 'detail', decimals: 0, defaultAgg: 'max', minW: 135, excelWidth: 14, tooltipKey: 'reports.tooltips.usedInNFg' },

    // ── Técnicas ──────────────────────────────────────────────────────────────
    // FAMILIA y SUBFAMILIA se quedan porque son el candidato a `Component Product
    // Group Name`, que Sterling pidió en su plantilla y sigue sin cumplirse:
    // `meInventsubfamily` viene poblada en las 678 filas y cubre buena parte de
    // sus categorías (cuellos, tapones, válvulas, etiquetas…). NO se promueve a
    // columna de cliente porque el campo mezcla categoría con MARCA —CLUB DE NUIT
    // y ARMAF aparecen como si fueran categorías—, así que hasta mapearlo contra
    // su lista se queda como técnica.
    { key: 'meInventfamily', label: 'FAMILY', tier: 'technical', kind: 'text', level: 'detail', minW: 170, excelWidth: 22 },
    { key: 'meInventsubfamily', label: 'SUBFAMILY', tier: 'technical', kind: 'text', level: 'detail', minW: 170, excelWidth: 22 },
  ],
}

// ── Consumo de producción ─────────────────────────────────────────────────────
//
// Existe porque Sterling lo reclamó por escrito y estaba frenando sus pedidos de
// aceite: quería comprobar por sí mismo cuántos kilos de fragancia se consumen de
// verdad frente a los que dice el escandallo. La respuesta del dato es que los
// aceites salen clavados y que el desvío está en el etanol, y la pantalla tiene
// que dejar eso a la vista sin que nadie lo explique de palabra.
//
// TRES NIVELES, y es el primer report con dos tipos de detalle que responden a
// preguntas distintas: el GRANEL dice DE DÓNDE salió el granel (qué orden lo
// fabricó, cuánto sobró) y el COMPONENTE dice QUÉ LLEVABA DENTRO (cada aceite con
// su previsto y su real). Comparten juego de columnas, así que las de granel salen
// vacías en las filas de componente y al revés; lo que evita que eso se lea como
// un dato ausente es la banda que separa los dos grupos (`detailBands`).
//
// AQUÍ NO SE DEDUPLICA, al contrario que en cobertura de materiales, y la
// diferencia es de fondo: allí se totaliza STOCK, que existe una vez y aparece
// repetido bajo cada terminado; aquí se totaliza CONSUMO, que ocurre una vez por
// producción. El etanol aparece en 17 terminados con 17 valores distintos, así que
// sumarlos es correcto — deduplicar perdería el 70 % de la cifra.

const CONSUMO_PRODUCCION: ReportDef = {
  key: 'consumoProduccion',
  route: 'consumo-produccion',
  titleKey: 'reports.consumoProduccion.title',
  tabKey: 'reports.consumoProduccion.tab',
  unitKey: 'reports.consumoProduccion.unit',
  headerType: 'FINISHED_GOOD',
  detailTypes: ['BULK', 'COMPONENT'],
  blockKeyFields: ['fgCode'],
  detailKeyFields: { BULK: ['granelCode', 'granelLote'], COMPONENT: ['componente'] },
  nestedDetail: true,
  wrapHeaders: true,
  levelOptions: [
    { tipoFila: 'FINISHED_GOOD', labelKey: 'reports.consumoProduccion.levelFg' },
    { tipoFila: 'BULK', labelKey: 'reports.consumoProduccion.levelBulk' },
    { tipoFila: 'COMPONENT', labelKey: 'reports.consumoProduccion.levelComponent' },
  ],
  storageKey: 'reports.consumoProduccion',
  excelBase: 'airval_report_consumo_produccion',
  distinctField: 'fgCode',
  sectionField: 'esSemielaborado',
  // La sección viene como 0/1 y se rotula al leerla. El Excel lleva además la
  // marca SEMI en su propia columna de control, porque ahí un 1 no diría nada.
  sectionLabels: { '0': 'FINISHED GOODS', '1': 'SEMI-FINISHED' },
  blockSummary: header => {
    const chips: ReportSummaryChip[] = []
    // El desvío primero, que es la pregunta del cliente. El corte del 1 % separa
    // el ruido del dato: en el producto que él puso como ejemplo deja fuera sus
    // cuatro aceites (+0,04 % a +0,80 %) y deja el etanol (+4,79 %).
    const pct = Number(header.desvioPct ?? NaN)
    if (Number.isFinite(pct) && Math.abs(pct) > PCT_DESVIO_RELEVANTE) {
      chips.push({
        labelKey: 'reports.summary.deviation',
        value: Math.round(pct * 100) / 100,
        tone: 'alert',
      })
    }
    // La sustitución se avisa en el bloque PLEGADO porque cambia cómo se lee el
    // desvío: un +2,6 % con cuatro sustituciones detrás no es el mismo dato que un
    // +2,6 % limpio, y el detalle que lo explica está veinte columnas a la derecha.
    const subs = Number(header.numSustituciones ?? 0)
    if (subs > 0) {
      chips.push({ labelKey: 'reports.summary.substitutions', value: subs, tone: 'alert' })
    }
    // Ni lotes ni componentes: sólo los AVISOS. Los recuentos costaban 90 px de
    // la columna del código —que es congelada, el espacio más caro de la tabla— y
    // al desplegar el bloque los enseña la subtabla de lotes con su propio total.
    // Un aviso, en cambio, sólo se ve si está aquí.
    return chips
  },
  filters: [
    { key: 'fgCode', labelKey: 'reports.filters.fgCode', kind: 'multiText', scope: 'header', field: 'fgCode', descripcionField: 'fgName', esArticulo: true },
    // Mismo patrón que el de componente y el de terminado: el código en el
    // valor y el lote al lado en el desplegable, para poder buscar por él.
    // El maestro de granel no trae descripción en la vista, así que el lote es
    // lo que hay: en cuanto la vista publique el nombre, se cambia aquí.
    { key: 'granelCode', labelKey: 'reports.filters.granelCode', kind: 'multiText', scope: 'detail', field: 'granelCode', descripcionField: 'granelLote', esArticulo: true },
    { key: 'componente', labelKey: 'reports.filters.componente', kind: 'multiText', scope: 'detail', field: 'componente', descripcionField: 'componenteDesc', esArticulo: true },
    {
      key: 'tipoComponente', labelKey: 'reports.filters.tipoComponente', kind: 'multiSelect', scope: 'detail', field: 'tipoComponente',
      optionLabelPrefix: 'reports.tipoComponente',
    },
    { key: 'esSemielaborado', labelKey: 'reports.filters.esSemielaboradoConsumo', kind: 'bool', scope: 'header', field: 'esSemielaborado' },
    // El flag lo calcula la vista sobre el desvío TOTAL del producto. La primera
    // versión miraba «algún componente con desvío» y marcaba los 20 productos de
    // 20, con lo que el filtro no filtraba nada.
    { key: 'esDesvioRelevante', labelKey: 'reports.filters.esDesvioRelevante', kind: 'bool', scope: 'header', field: 'esDesvioRelevante' },
  ],
  cols: [
    // ── El producto ───────────────────────────────────────────────────────────
    // Ancho de sobra para alojar los chips del bloque plegado junto al código.
    // Es la única congelada del report: las identidades de los dos niveles de
    // detalle viven dentro de su subtabla y no necesitan quedarse fijas.
    { key: 'fgCode', label: 'FG CODE', tier: 'client', kind: 'text', esArticulo: true, frozen: true, minW: 200, excelWidth: 18 },
    // PEGADA AL CÓDIGO, y por eso `client` y no `control`: existe sólo para
    // deshacer una ambigüedad del código de al lado, y treinta columnas más a la
    // derecha no desharía nada. Es el mismo motivo por el que EXCLUDED (OTHER
    // UNIT) va junto al total y no al final.
    //
    // El cliente ve los Armaf sin su letra de tipo —regla suya—, así que
    // `XARF32101586` y `ARF32101586` se le enseñan iguales, con distinto nombre y
    // distinta cantidad. Esta columna dice cuál es cuál sin romper su convención.
    {
      key: 'tipoArticulo', label: 'ITEM TYPE', tier: 'client', kind: 'text',
      level: 'header', minW: 105, excelWidth: 16,
      // La vista emite `VARIANT`, igual de vago a propósito: sabemos que no son el
      // artículo estándar y no sabemos qué son. Llamarlos «muestra» o «retrabajo»
      // sería escribir una conjetura en un informe que firma el cliente.
      valueLabelPrefix: 'reports.tipoArticulo',
      tooltipKey: 'reports.tooltips.tipoArticulo',
    },
    { key: 'fgName', label: 'FG NAME', tier: 'client', kind: 'text', level: 'header', minW: 230, excelWidth: 38 },
    // 105 px: «PRODUCIDAS» no cabía en 100 y quedaba a un pelo de salirse.
    { key: 'pcsProducidas', label: 'UNITS PRODUCED', tier: 'client', kind: 'num', level: 'header', decimals: 0, defaultAgg: 'sum', minW: 105, excelWidth: 16 },
    { key: 'litrosGranel', label: 'BULK USED (L)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 105, excelWidth: 16 },

    // ── El reparto en kilos: la pregunta del cliente ──────────────────────────
    { key: 'kgFragancia', label: 'FRAGRANCE OIL (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 110, excelWidth: 18 },
    { key: 'kgDisolvente', label: 'SOLVENT (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 105, excelWidth: 16 },
    { key: 'kgIngrediente', label: 'OTHER INGREDIENTS (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 120, excelWidth: 20 },
    { key: 'kgTotal', label: 'TOTAL (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 105, excelWidth: 14, tooltipKey: 'reports.tooltips.kgTotal' },
    // PEGADAS AL TOTAL, y por eso `client` y no `control`: el total en kg suma
    // sólo lo que viene en kg, y sin ver al lado lo que se ha quedado fuera —el
    // agua desionizada viene en litros— ese total se lee como si fuera todo.
    { key: 'cantOtraUnidad', label: 'EXCLUDED (OTHER UNIT)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 105, excelWidth: 20, tooltipKey: 'reports.tooltips.cantOtraUnidad' },
    { key: 'unidadesNoKg', label: 'EXCLUDED UNITS', tier: 'client', kind: 'text', level: 'header', minW: 90, excelWidth: 16 },

    // ── Previsto contra real ──────────────────────────────────────────────────
    { key: 'kgBomTotal', label: 'BOM TOTAL (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 105, excelWidth: 18 },
    { key: 'desvioKg', label: 'TOTAL DEVIATION (KG)', tier: 'client', kind: 'num', level: 'header', defaultAgg: 'sum', minW: 110, excelWidth: 18 },
    { key: 'desvioPct', label: 'TOTAL DEVIATION %', tier: 'client', kind: 'pct', tone: 'deviation', level: 'header', decimals: 2, defaultAgg: 'avg', minW: 105, excelWidth: 14, tooltipKey: 'reports.tooltips.desvioPct' },
    { key: 'mlPorPieza', label: 'ML PER UNIT', tier: 'client', kind: 'num', level: 'header', minW: 95, excelWidth: 14 },
    // Va aquí y no con las nuestras porque es la pareja de ML PER UNIT y porque es
    // la ratio que más le dice a un proveedor de aceite: cuántos gramos de esencia
    // lleva cada frasco. Estuvo saliendo con guion en las 20 filas hasta que se
    // declaró en el modelo Pydantic — la vista sí la tenía.
    {
      key: 'grFraganciaPorPieza', label: 'FRAGRANCE G/UNIT', tier: 'client', kind: 'num',
      level: 'header', decimals: 4, defaultAgg: 'avg', minW: 110, excelWidth: 18,
      tooltipKey: 'reports.tooltips.grFraganciaPorPieza',
    },

    // ── De dónde salió el granel ──────────────────────────────────────────────
    // El código antes del lote: primero QUÉ granel es y después cuál de sus
    // lotes, que es el orden en que se lee cualquier pareja código/detalle en
    // los otros tres informes.
    { key: 'granelCode', label: 'BULK CODE', tier: 'client', kind: 'text', level: 'detail', detailType: 'BULK', esArticulo: true, minW: 110, excelWidth: 18 },
    { key: 'granelLote', label: 'BULK BATCH', tier: 'client', kind: 'text', level: 'detail', detailType: 'BULK', minW: 110, excelWidth: 16 },
    { key: 'ordenGranel', label: 'BULK ORDER', tier: 'client', kind: 'text', level: 'detail', detailType: 'BULK', minW: 115, excelWidth: 18 },
    { key: 'litrosProducidos', label: 'LITRES PRODUCED', tier: 'client', kind: 'num', level: 'detail', detailType: 'BULK', defaultAgg: 'sum', minW: 105, excelWidth: 18 },
    { key: 'litrosConsumidos', label: 'LITRES USED', tier: 'client', kind: 'num', level: 'detail', detailType: 'BULK', defaultAgg: 'sum', minW: 105, excelWidth: 16 },
    { key: 'litrosSobrantes', label: 'LITRES LEFT', tier: 'client', kind: 'num', level: 'detail', detailType: 'BULK', defaultAgg: 'sum', minW: 100, excelWidth: 16 },
    // El «Extra semi → to be used in next production (specify pcs)» que el cliente
    // pidió con esas palabras.
    { key: 'pcsEquivalentesSobrante', label: 'SURPLUS IN PCS', tier: 'client', kind: 'num', level: 'detail', detailType: 'BULK', decimals: 0, defaultAgg: 'sum', minW: 100, excelWidth: 16 },

    // ── Qué llevaba dentro ────────────────────────────────────────────────────
    { key: 'componente', label: 'COMPONENT', tier: 'client', kind: 'text', level: 'detail', detailType: 'COMPONENT', esArticulo: true, minW: 115, excelWidth: 18 },
    { key: 'componenteDesc', label: 'COMPONENT DESCRIPTION', tier: 'client', kind: 'text', level: 'detail', detailType: 'COMPONENT', minW: 340, excelWidth: 38 },
    {
      key: 'tipoComponente', label: 'COMPONENT TYPE', tier: 'client', kind: 'text',
      level: 'detail', detailType: 'COMPONENT', minW: 110, excelWidth: 18,
      valueLabelPrefix: 'reports.tipoComponente',
      tooltipKey: 'reports.tooltips.tipoComponente',
    },
    { key: 'kgPrevistoBom', label: 'BOM QTY (KG)', tier: 'client', kind: 'num', level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 105, excelWidth: 16 },
    { key: 'kgReal', label: 'ACTUAL QTY (KG)', tier: 'client', kind: 'num', level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 105, excelWidth: 18, tooltipKey: 'reports.tooltips.kgReal' },
    { key: 'kgDesvio', label: 'DEVIATION (KG)', tier: 'client', kind: 'num', level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 105, excelWidth: 18 },
    { key: 'kgDesvioPct', label: 'DEVIATION %', tier: 'client', kind: 'pct', tone: 'deviation', toneSuppressedBy: 'avisoSustitucion', level: 'detail', detailType: 'COMPONENT', decimals: 2, defaultAgg: 'avg', minW: 95, excelWidth: 14 },
    // Detrás del desvío, y del CLIENTE: es la respuesta a «¿y lo que se pierde en
    // el proceso?», que es lo primero que se pregunta al ver un desvío. En el
    // producto que el cliente puso como ejemplo, de 269,80 kg de desvío del etanol
    // 267,49 están declarados como merma de proceso.
    {
      // Sin `level`, así que vale para la cabecera Y para las sublíneas; el
      // `detailType` la limita a las de componente, que son las únicas del detalle
      // que la traen. Estuvo declarada `level: 'detail'` y en la fila del producto
      // salía vacía aunque el dato venía: 20 de 20 filas de producto poblado.
      key: 'kgMerma', label: 'PROCESS LOSS (KG)', tier: 'client', kind: 'num',
      detailType: 'COMPONENT', defaultAgg: 'sum', minW: 110, excelWidth: 18,
      tooltipKey: 'reports.tooltips.kgMerma',
    },
    // ── La otra base: la orden de granel completa ─────────────────────────────
    //
    // Detrás del desvío de lo atribuido, y las tres juntas a propósito: comparar
    // el real de una base contra el previsto de la otra convierte granel sin
    // envasar en un sobreconsumo falso de ~3 %, así que el total va con SU
    // previsto al lado y con la diferencia nombrada.
    //
    // Sólo en la fila del componente, no en la del producto: el consumo de orden
    // completa NO es aditivo entre productos —una esencia compartida se cuenta
    // entera en cada uno— y dentro de la subtabla de un producto no hay forma de
    // sumarla cruzando productos.
    {
      key: 'kgRealGranelTotal', label: 'BULK ORDER ACTUAL (KG)', tier: 'client', kind: 'num',
      level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 115, excelWidth: 22,
      tooltipKey: 'reports.tooltips.kgRealGranelTotal',
    },
    {
      key: 'kgBomGranelTotal', label: 'BULK ORDER BOM (KG)', tier: 'client', kind: 'num',
      level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 115, excelWidth: 22,
      tooltipKey: 'reports.tooltips.kgBomGranelTotal',
    },
    {
      key: 'kgEnSobrante', label: 'STILL IN BULK (KG)', tier: 'client', kind: 'num',
      level: 'detail', detailType: 'COMPONENT', defaultAgg: 'sum', minW: 105, excelWidth: 20,
      tooltipKey: 'reports.tooltips.kgEnSobrante',
    },
    // La sustitución de material, en la fila que la provoca. Va con las columnas
    // del cliente y no con las nuestras porque responde a una pregunta suya —«por
    // qué consumís algo que no está en mi escandallo»— y porque sin ella un
    // componente con previsto 1.027 y real 0 se lee como un fallo del informe.
    {
      key: 'avisoSustitucion', label: 'SUBSTITUTION', tier: 'client', kind: 'text',
      level: 'detail', detailType: 'COMPONENT', minW: 130, excelWidth: 20,
      valueLabelPrefix: 'reports.sustitucion',
      tooltipKey: 'reports.tooltips.avisoSustitucion',
    },

    // ── Nuestras columnas de control ──────────────────────────────────────────
    { key: 'numOrdenes', label: 'PRDN ORDERS', tier: 'control', kind: 'num', level: 'header', decimals: 0, defaultAgg: 'sum', minW: 90, excelWidth: 14, tooltipKey: 'reports.tooltips.numOrdenes' },
    {
      // Decide cómo se leen las DOS columnas de sobrante del lote. SOBRANTES es
      // «producidos menos lo envasado en ESTE producto», así que en un lote que
      // alimenta a varios, lo que envasó el otro aparece aquí como sobrante:
      // medido, el lote 260015E sale con 4.504 L «sobrantes» en un bloque que ya
      // están envasados en el otro. Sin esta marca no hay con qué distinguir un
      // sobrante real de ese reparto.
      //
      // Declarada aquí y no junto a SOBRANTES porque las columnas del cliente van
      // TODAS antes que las nuestras —es el orden de su Excel, y hay un test que
      // lo vigila—. En pantalla no se pierde nada: dentro de la subtabla de lotes
      // no hay ninguna columna del cliente después, así que sale pegada a ella.
      //
      // Marca NEUTRA a propósito: en este informe el ámbar ya significa «desvío
      // relevante», y darle un segundo significado en la misma tabla se lee peor
      // que un distintivo gris con su explicación al lado.
      key: 'comparteGranel', label: 'SHARED BULK', tier: 'control', kind: 'flag',
      level: 'detail', detailType: 'BULK', flagOn: 1, flagText: 'SHARED',
      minW: 125, excelWidth: 14, tooltipKey: 'reports.tooltips.comparteGranel',
    },
    {
      key: 'numSustituciones', label: 'SUBSTITUTIONS', tier: 'control', kind: 'count',
      // 130 px porque el rótulo es UNA palabra —SUSTITUCIONES / SUBSTITUTIONS—
      // y no hay dónde partirla: con menos se sale de su columna. El dato son
      // dos dígitos, así que el ancho lo manda el rótulo, no la cifra.
      level: 'header', decimals: 0, minToShow: 1, defaultAgg: 'sum', minW: 130, excelWidth: 16,
      tooltipKey: 'reports.tooltips.numSustituciones',
    },
    // Visible, y no técnica: el agua desionizada viene en litros y el resto en
    // kilos, así que esconder la unidad invita a sumar la columna de reales sin
    // mirar. Es la misma razón por la que EXCLUDED (OTHER UNIT) va pegada al total.
    { key: 'componenteUnidad', label: 'COMPONENT UNIT', tier: 'control', kind: 'text', level: 'detail', detailType: 'COMPONENT', minW: 95, excelWidth: 16 },

    // Sólo para el Excel: en pantalla lo dicen la banda de sección y TIPO DE
    // ARTÍCULO, pero el fichero no tiene bandas y sin esta columna quien lo abre
    // no puede distinguir las filas de granel.
    {
      key: 'esSemielaborado', label: 'SEMI-FINISHED', kind: 'flag', tier: 'control',
      level: 'header', excelOnly: true, flagOn: 1, flagText: 'SEMI', excelWidth: 14,
    },
  ],
}

export const REPORT_DEFS: Record<ReportKey, ReportDef> = {
  rawMaterials: RAW_MATERIALS,
  fg: FG,
  fgToRm: FG_TO_RM,
  consumoProduccion: CONSUMO_PRODUCCION,
}

/** En el orden en que aparecen las pestañas. */
export const REPORT_ORDER: ReportKey[] = ['rawMaterials', 'fg', 'fgToRm', 'consumoProduccion']

export function reportByRoute(route: string): ReportDef | undefined {
  return REPORT_ORDER.map(k => REPORT_DEFS[k]).find(d => d.route === route)
}

/**
 * ¿Se pinta esta columna en una fila de este nivel?
 *
 * `tipoFila` afina el nivel de detalle cuando hay más de uno: una columna que
 * declara `detailType` sólo aplica a las filas de ESE tipo. Sin pasarlo, se
 * comporta como antes y una columna de detalle aplica a todo el detalle — es lo
 * que hacen los reports con un único tipo de sublínea.
 */
export function colAppliesTo(col: ReportCol, isHeader: boolean, tipoFila?: string): boolean {
  const level = col.level ?? 'both'
  if (level === 'header') return isHeader
  if (level === 'detail' && isHeader) return false
  // Detalle (o `both` en una sublínea): respeta el tipo declarado si lo hay.
  if (!isHeader && col.detailType && tipoFila) return col.detailType === tipoFila
  return true
}

/** Columnas que pertenecen a un tipo de detalle concreto, en orden de tabla. */
export function colsOfDetailType(cols: ReportCol[], tipoFila: string): ReportCol[] {
  return cols.filter(c => c.detailType === tipoFila)
}

/**
 * Columnas de la tabla MAESTRA con detalle anidado: las que valen en la fila de
 * cabecera.
 *
 * Las de un tipo de detalle concreto salen de aquí porque se pintan dentro de su
 * subtabla. Las que valen en los dos niveles —la merma, que está en el producto y
 * en cada componente— se quedan en las dos, y es lo que se quiere: la cifra del
 * producto arriba y su desglose al desplegar.
 */
export function masterCols(def: ReportDef, cols: ReportCol[]): ReportCol[] {
  if (!def.nestedDetail) return cols
  return cols.filter(c => colAppliesTo(c, true, def.headerType))
}

/** Las numéricas llevan botón Σ y van alineadas a la derecha. */
export function isNumericCol(col: ReportCol): boolean {
  return col.kind === 'num' || col.kind === 'pct' || col.kind === 'count'
}

/** Rótulo de una sección, con el texto del cliente; si no hay, el valor crudo. */
/**
 * Rótulo de PANTALLA de una columna: sigue el toggle ES/EN de la barra lateral.
 *
 * `col.label` es otra cosa y no se toca: es el literal exacto del Excel del
 * cliente, en inglés, y es el que se escribe en la exportación. Aquí solo se
 * traduce lo que se ve en la app, que es UI nuestra. Sin traducción declarada en
 * `reports.cols.<clave>`, cae al literal inglés — así una columna nueva sale
 * legible desde el primer día en vez de mostrar la clave i18n.
 */
export function colLabel(col: ReportCol, t: TFunction): string {
  return t(`reports.cols.${col.key}`, { defaultValue: col.label })
}

/**
 * Valor de PANTALLA de una celda de texto, en el idioma de la UI.
 *
 * Las vistas emiten el vocabulario del Excel del cliente —`FRAGRANCE`,
 * `Reported as finished`—, y en una tabla cuyos rótulos SÍ siguen el selector
 * ES/EN un valor sin traducir canta: la interfaz española enseñaría «Fragrance»
 * bajo la columna «TIPO». Las claves i18n van indexadas por el valor que emite
 * la vista, así que renombrar un valor obliga a renombrar su clave.
 */
export function colValueUi(col: ReportCol, texto: string, t: TFunction): string {
  if (texto === '') return texto
  const enIngles = col.valueLabels?.[texto]
  if (col.valueLabelPrefix) {
    return t(`${col.valueLabelPrefix}.${texto}`, { defaultValue: enIngles ?? texto })
  }
  return enIngles ?? texto
}

/**
 * Rótulo de PANTALLA de una sección, en el idioma de la UI.
 *
 * Mismo reparto que en las columnas: `sectionLabel` es el del Excel (inglés del
 * cliente) y este el de la app. La banda se pinta en versalitas por CSS, así que
 * la traducción no necesita ir en mayúsculas.
 */
export function sectionLabelUi(def: ReportDef, value: unknown, t: TFunction): string {
  return t(`reports.seccion.${String(value)}`, { defaultValue: sectionLabel(def, value) })
}

export function sectionLabel(def: ReportDef, value: unknown): string {
  const raw = String(value ?? '')
  return def.sectionLabels?.[raw] ?? raw
}
